import { useState, useRef, useEffect } from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import { useValidateProjectData } from '@material-bank/projects-utils';
import { CustomIcon } from 'components/CustomIcon';
import { useIntl } from 'react-intl';
import useSWRMutation from 'swr/mutation';
import * as Styled from './ChangeProjectDropDown.styles';
import { MAX_PROJECTS, API } from '../../config/digitalSampling.config';
import { definedTranslation } from '../../i18n';
import {
  getCurrentProjectData,
  currentProjectUpdate,
  getUserOrderType,
} from '../../utils';

interface ChangeProjectDropDownProps {
  projects: Record<string, ProjectModel>;
  showSelectedProject?: boolean;
  currentProject: ProjectModel | null;
  openDirection?: 'bottom' | 'top'; // The direction the dropdown opens relative to the button
  onProjectChange?: (projectId: string) => void;
}

export const ChangeProjectDropDown = ({
  projects,
  showSelectedProject = false,
  openDirection = 'bottom',
  currentProject,
  onProjectChange,
}: ChangeProjectDropDownProps) => {
  const t = useIntl();
  const { isProjectDataComplete } = useValidateProjectData();

  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [shouldUpdate, setShouldUpdate] = useState(false);

  const { isNonSampleUser } = getUserOrderType();

  const { trigger, data, error } = useSWRMutation(
    API.projectSetList,
    currentProjectUpdate,
    {
      revalidate: false,
    },
  );

  const handleProjectClick = (projectId: string) => {
    setIsOpen(false);

    const selectedProject = projects[projectId];

    if (!selectedProject || !projectId) {
      console.error(`Project with ID ${projectId} not found.`);

      return;
    }

    const updateProject = () => {
      setShouldUpdate(true);
      trigger({ project_id: parseInt(projectId, 10) });

      if (onProjectChange && selectedProject) {
        onProjectChange(selectedProject.externalId.toString());
      }

      dataLayer.push({
        event: 'hpauth_change_proj',
        current_proj_id: currentProject?.externalId,
        updated_proj_id: projectId,
        interaction_location: 'projects_header',
      });
    };

    if (selectedProject && !isProjectDataComplete(selectedProject)) {
      projectsEditDialogConfig.openProjectEditDialog({
        projectId: selectedProject.externalId,
        successCallback: updateProject,
      });

      return;
    }

    updateProject();
  };

  useEffect(() => {
    if (data?.status === 'success' && shouldUpdate) {
      window.dispatchEvent(new CustomEvent('reload-customer-section-data'));
    }

    setShouldUpdate(false);
  }, [data, error, shouldUpdate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        // TODO: validate the intention of the code and fix the type
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        !panelRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleCreateNewProject = () => {
    window.dispatchEvent(
      new CustomEvent('open-create-project-modal', {
        detail: {
          showModalAfterCreate: false,
          eventLocation: 'Homepage',
        },
      }),
    );
    setIsOpen(false);

    const projectData = getCurrentProjectData();

    dataLayer.push({
      event: isNonSampleUser
        ? 'hpauthnonsampling_create_new_proj'
        : 'hpauth_create_new_proj',
      proj_creation_method: 'change_proj_cta',
      project_id: projectData?.projectId,
      project_name: projectData?.projectId,
      interaction_location: 'projects_header',
    });
  };

  /**
   * Sort projects by the following order:
   * 1. the current project
   * 2. active projects (sorted by lastUpdated descending - most recent first)
   */
  const sortedProjects = Object.values(projects).sort((a, b) => {
    // Current project goes first
    if (a.externalId.toString() === currentProject?.externalId?.toString()) {
      return -1;
    }

    if (b.externalId.toString() === currentProject?.externalId?.toString()) {
      return 1;
    }

    if (a.updatedAt && b.updatedAt) {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }

    return 0;
  });

  return (
    <div className="relative">
      <Styled.ToggleButton onClick={handleToggle} type="button" isOpen={isOpen}>
        {showSelectedProject ? (
          <Styled.ProjectTextWrapper>
            <Styled.ProjectText>
              {t.formatMessage(
                definedTranslation.catalogDownloadsPage.projectLabel,
              )}
              :
            </Styled.ProjectText>{' '}
            {currentProject?.title}
          </Styled.ProjectTextWrapper>
        ) : (
          <span>
            {t.formatMessage(definedTranslation.previewFiles.changeProject)}
          </span>
        )}{' '}
        <CustomIcon name={!isOpen ? 'arrow-down' : 'arrow-up'} />
      </Styled.ToggleButton>
      {isOpen ? (
        <Styled.PanelWrapper openDirection={openDirection} ref={panelRef}>
          <Styled.ProjectList>
            {sortedProjects.map((project, index) =>
              index < MAX_PROJECTS ? (
                <li key={project.externalId}>
                  <Styled.ExistingProjectButton
                    isCurrentProject={
                      project.externalId === currentProject?.externalId
                    }
                    onClick={() => {
                      handleProjectClick(project.externalId.toString());
                    }}
                  >
                    {project.title}
                  </Styled.ExistingProjectButton>
                </li>
              ) : null,
            )}
          </Styled.ProjectList>
          <Styled.CreateProjectButton
            onClick={handleCreateNewProject}
            type="button"
          >
            <CustomIcon name={'add'} />{' '}
            {t.formatMessage(definedTranslation.previewFiles.createANewProject)}
          </Styled.CreateProjectButton>
        </Styled.PanelWrapper>
      ) : null}
    </div>
  );
};
